import { Component } from '@theme/component';
import { debounce, prefersReducedMotion } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogOpenEvent, DialogComponent } from '@theme/dialog';

const MAX_RECENTLY_VIEWED_PRODUCTS = 4;
const SEARCH_RESOURCE_TYPES = ['product', 'collection', 'article'];
const SEARCH_COUNT_RESOURCE_TYPES = ['product', 'article'];
const PRODUCT_SEARCH_FIELDS = 'title,product_type,vendor,tag,variants.title,variants.sku';

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @property {HTMLButtonElement[]} [resourceTabs] - The resource tab buttons.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  /**
   * @type {AbortController | null}
   */
  #activeCountFetch = null;

  #emptyStateLoaded = false;

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal, once: true });

      this.addEventListener('click', this.#handleModalClick, { signal });
    }

    if (RecentlyViewed.getProducts().length > 0) {
      requestIdleCallback(() => {
        this.#loadEmptyState();
      });
    }
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  #handleDialogOpen = () => {
    if (!this.#emptyStateLoaded && RecentlyViewed.getProducts().length > 0) {
      this.#loadEmptyState();
    }
  };

  #loadEmptyState() {
    if (this.#emptyStateLoaded) return;
    this.#emptyStateLoaded = true;
    this.resetSearch(false);
  }

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__resource-list, ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(
          container.querySelectorAll(
            '[ref="resultsItems[]"], [ref="recentlyViewedItems[]"], .predictive-search-results__card'
          )
        );
      })
      .filter((item) => item instanceof HTMLElement && item.getClientRects().length > 0);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    let activeItem = null;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');
        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        activeItem = item;
      } else {
        item.removeAttribute('aria-selected');
      }
    }

    activeItem?.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    // Enter must always be handled (even with zero visible result rows). Otherwise the
    // browser submits the form without going through #buildSearchPageUrl, and mixed
    // default search types inflate the PLP "N Products" count while the grid stays empty.
    if (event.key === 'Enter') {
      event.preventDefault();

      const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
      if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
        window.location.href = singleResultContainer.dataset.singleResultUrl;
        return;
      }

      if (this.#allResultsItems?.length && this.#currentIndex >= 0) {
        this.#currentItem?.querySelector('a')?.click();
        return;
      }

      const searchTerm = this.#normalizeSearchTerm(this.refs.searchInput.value);
      if (!searchTerm.length) return;

      window.location.href = this.#buildSearchPageUrl(searchTerm, this.#activeResourceType).toString();
      return;
    }

    if (!this.#allResultsItems?.length) {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'Tab':
        if (event.shiftKey) {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        } else {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        }
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;
    }
  };

  /**
   * Intercept native form submit so search always includes type/options params.
   * @param {SubmitEvent} event - The submit event.
   */
  onSearchSubmit = (event) => {
    event.preventDefault();

    const searchTerm = this.#normalizeSearchTerm(this.refs.searchInput.value);
    if (!searchTerm.length) return;

    window.location.href = this.#buildSearchPageUrl(searchTerm, this.#activeResourceType).toString();
  };

  /**
   * Selects a desktop resource tab.
   * @param {Event} event - The click event.
   */
  selectResourceTab = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const tab = target.closest('[data-predictive-search-tab]');
    if (!(tab instanceof HTMLButtonElement)) return;

    this.#activateResourceTab(tab.dataset.predictiveSearchTab);
  };

  /**
   * Handles keyboard navigation between desktop resource tabs.
   * @param {KeyboardEvent} event - The keydown event.
   */
  onResourceTabKeyDown = (event) => {
    const currentTab = event.currentTarget;
    if (!(currentTab instanceof HTMLButtonElement)) return;

    const tabs = this.#resourceTabs;
    const currentIndex = tabs.indexOf(currentTab);
    if (currentIndex < 0) return;

    let nextIndex = currentIndex;

    switch (event.key) {
      case 'ArrowLeft':
        nextIndex = currentIndex > 0 ? currentIndex - 1 : tabs.length - 1;
        break;
      case 'ArrowRight':
        nextIndex = currentIndex < tabs.length - 1 ? currentIndex + 1 : 0;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = tabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    tabs[nextIndex]?.focus();
    this.#activateResourceTab(tabs[nextIndex]?.dataset.predictiveSearchTab);
  };

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce((event) => {
    // If the input is not a text input (like using the Escape key), don't search
    if (!event.inputType) return;

    const searchTerm = this.refs.searchInput.value.trim();
    const normalizedSearchTerm = this.#normalizeSearchTerm(searchTerm);
    this.#currentIndex = -1;

    if (!normalizedSearchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(normalizedSearchTerm);
  }, 200);

  /**
   * Normalizes typed SKU searches so "SKU: 1615" or "SKU. 1615" searches the actual SKU value.
   * @param {string} value - The entered search term
   * @returns {string}
   */
  #normalizeSearchTerm(value) {
    return value.trim().replace(/^sku\s*[.:]\s*/i, '').trim();
  }

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner')?.scrollTo(0, 0);
      this.querySelector('.predictive-search-form__content')?.scrollTo(0, 0);
    });
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[type]', 'query,product,collection,article');
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('resources[options][fields]', PRODUCT_SEARCH_FIELDS);

    const { predictiveSearchResults } = this.refs;

    const abortController = this.#createAbortController();

    sectionRenderer
      .getSectionHTML(this.dataset.sectionId, false, url)
      .then((resultsMarkup) => {
        if (!resultsMarkup) return;

        if (abortController.signal.aborted) return;

        morph(predictiveSearchResults, resultsMarkup);

        this.#resetScrollPositions();
        this.#updateResultCounts(searchTerm, abortController);
      })
      .catch((error) => {
        if (abortController.signal.aborted) return;
        throw error;
      });
  }

  /**
   * Fetch total result counts and update headings / view-all link text.
   * @param {string} searchTerm - The term to search for
   * @param {AbortController} abortController - Abort controller for the search request
   */
  async #updateResultCounts(searchTerm, abortController) {
    if (this.#activeCountFetch) {
      this.#activeCountFetch.abort();
    }

    const countAbortController = new AbortController();
    this.#activeCountFetch = countAbortController;

    try {
      const resourceTypes = this.#resourceTypesWithResults.filter((resourceType) =>
        SEARCH_COUNT_RESOURCE_TYPES.includes(resourceType)
      );
      const countResults = await Promise.all(
        resourceTypes.map(async (resourceType) => ({
          resourceType,
          count: await this.#getResultCount(searchTerm, resourceType),
        }))
      );

      if (abortController.signal.aborted || countAbortController.signal.aborted) return;

      let totalCount = 0;
      countResults.forEach(({ resourceType, count }) => {
        const numericCount = Number(count);
        if (count === null || !Number.isFinite(numericCount) || numericCount < 0) return;

        if (resourceType === 'product') {
          this.#setProductSearchActionsVisibility(numericCount > 0);
        }

        if (numericCount === 0) return;

        totalCount += numericCount;
        this.#applyResultCount(resourceType, numericCount.toString());
      });

      this.#updateLiveRegion(totalCount, searchTerm);
    } catch (error) {
      if (countAbortController.signal.aborted) return;
      throw error;
    }
  }

  /**
   * Toggle product count labels and view-all links based on the full storefront search count.
   * Predictive Search can return valid product suggestions that storefront /search does not
   * count. Keep those suggestions visible, but hide actions that would open an empty search page.
   * @param {boolean} isVisible - Whether full search has product results.
   */
  #setProductSearchActionsVisibility(isVisible) {
    const productSearchActions = this.querySelectorAll(
      '[data-predictive-search-count="product"], [data-predictive-search-view-all="product"]'
    );

    for (const element of productSearchActions) {
      if (!(element instanceof HTMLElement)) continue;
      element.hidden = !isVisible;
    }
  }

  /**
   * Build a full search-page URL that matches predictive search options.
   * Without type=product, mixed collection/article hits inflate the PLP "N products" count
   * while the product grid stays empty.
   * @param {string} searchTerm - Normalized search term
   * @param {string} [resourceType='product'] - Resource type for the search page
   * @returns {URL}
   */
  #buildSearchPageUrl(searchTerm, resourceType = 'product') {
    const searchUrl = new URL(Theme.routes.search_url, location.origin);
    const type = SEARCH_RESOURCE_TYPES.includes(resourceType) ? resourceType : 'product';

    searchUrl.searchParams.set('q', searchTerm);
    searchUrl.searchParams.set('type', type);
    searchUrl.searchParams.set('options[prefix]', 'last');

    if (type === 'product') {
      searchUrl.searchParams.set('options[fields]', PRODUCT_SEARCH_FIELDS);
    }

    return searchUrl;
  }

  /**
   * Currently active desktop resource tab, defaulting to product.
   * @returns {string}
   */
  get #activeResourceType() {
    const activeTab = this.#resourceTabs.find((tab) => tab.classList.contains('predictive-search-results__tab--active'));
    const resourceType = activeTab?.dataset.predictiveSearchTab;

    return resourceType && SEARCH_RESOURCE_TYPES.includes(resourceType) ? resourceType : 'product';
  }

  /**
   * Fetch total count for one search resource type.
   * @param {string} searchTerm - The term to search for
   * @param {string} resourceType - The resource type to count
   * @returns {Promise<string | null>}
   */
  async #getResultCount(searchTerm, resourceType) {
    const url = this.#buildSearchPageUrl(searchTerm, resourceType);
    const countMarkup = await sectionRenderer.getSectionHTML('predictive-search-count', false, url);
    const parsedMarkup = new DOMParser().parseFromString(countMarkup, 'text/html');
    const countElement = parsedMarkup.querySelector(`[data-predictive-search-count="${resourceType}"]`);

    return countElement?.getAttribute('data-predictive-search-count-value') ?? null;
  }

  /**
   * Apply a resource count to headings and view-all links.
   * @param {string} resourceType - Resource type.
   * @param {string} count - Total resource match count.
   */
  #applyResultCount(resourceType, count) {
    const countElements = this.querySelectorAll(`[data-predictive-search-count="${resourceType}"]`);
    const viewAllTemplate = this.#getViewAllTemplate(resourceType);
    const viewAllText = this.#getViewAllText(resourceType, count, viewAllTemplate);
    const viewAllTextElements = this.querySelectorAll(`[data-predictive-search-view-all-text="${resourceType}"]`);

    if (resourceType !== 'product') {
      countElements.forEach((countElement) => {
        if (!(countElement instanceof HTMLElement)) return;
        countElement.dataset.predictiveSearchCountValue = count;
        countElement.textContent = `(${count})`;
      });
    }

    if (viewAllText) {
      viewAllTextElements.forEach((viewAllTextElement) => {
        viewAllTextElement.textContent = viewAllText;
      });
    }
  }

  /**
   * Update the live region with the combined total result count.
   * @param {number} totalCount - Total results across rendered resource groups.
   * @param {string} searchTerm - Search term.
   */
  #updateLiveRegion(totalCount, searchTerm) {
    const liveRegion = this.querySelector('[data-predictive-search-live-region]');
    const resultsCountTemplate = this.dataset.resultsCountTemplate;

    if (!liveRegion || !resultsCountTemplate || totalCount <= 0) return;

    liveRegion.textContent = resultsCountTemplate.replace('__COUNT__', totalCount.toString()).replace('__QUERY__', searchTerm);
  }

  /**
   * Get visible resource types rendered in the current results markup.
   * @returns {string[]}
   */
  get #resourceTypesWithResults() {
    const resourceTypes = new Set(
      Array.from(this.querySelectorAll('[data-predictive-search-count]'))
        .map((element) => element.getAttribute('data-predictive-search-count'))
        .filter((resourceType) => resourceType && SEARCH_RESOURCE_TYPES.includes(resourceType))
    );

    return /** @type {string[]} */ (Array.from(resourceTypes));
  }

  /**
   * Get desktop resource tab buttons.
   * @returns {HTMLButtonElement[]}
   */
  get #resourceTabs() {
    return Array.from(this.querySelectorAll('[data-predictive-search-tab]')).filter(
      (tab) => tab instanceof HTMLButtonElement && !tab.hidden
    );
  }

  /**
   * Activate one resource tab and panel.
   * @param {string | undefined} resourceType - Resource type to activate.
   */
  #activateResourceTab(resourceType) {
    if (!resourceType) return;

    this.#resourceTabs.forEach((tab) => {
      const isActive = tab.dataset.predictiveSearchTab === resourceType;
      tab.classList.toggle('predictive-search-results__tab--active', isActive);
      tab.setAttribute('aria-selected', isActive.toString());
    });

    this.querySelectorAll('[data-predictive-search-panel]').forEach((panel) => {
      if (!(panel instanceof HTMLElement)) return;

      const isActive = panel.dataset.predictiveSearchPanel === resourceType;
      panel.classList.toggle('predictive-search-results__panel--active', isActive);
      panel.setAttribute('aria-hidden', (!isActive).toString());
    });
  }

  /**
   * Get the localized view-all template for a resource type.
   * @param {string} resourceType - Resource type.
   * @returns {string | undefined}
   */
  #getViewAllTemplate(resourceType) {
    switch (resourceType) {
      case 'product':
        return this.dataset.viewAllProductsTemplate;
      case 'collection':
        return this.dataset.viewAllCollectionsTemplate;
      case 'article':
        return this.dataset.viewAllArticlesTemplate;
      default:
        return undefined;
    }
  }

  /**
   * Get the localized view-all text for a resource type and count.
   * @param {string} resourceType - Resource type.
   * @param {string} count - Total resource match count.
   * @param {string | undefined} template - Localized view-all template.
   * @returns {string | undefined}
   */
  #getViewAllText(resourceType, count, template) {
    if (resourceType === 'product' && count === '1' && this.dataset.viewAllProductsSingleTemplate) {
      return this.dataset.viewAllProductsSingleTemplate;
    }

    return template?.replace('__COUNT__', count);
  }

  #showRecentlyViewedColumn() {
    const column = this.querySelector('#predictive-search-recently-viewed-column');
    if (column instanceof HTMLElement) {
      column.hidden = false;
    }
  }

  #hideRecentlyViewedColumn() {
    const column = this.querySelector('#predictive-search-recently-viewed-column');
    if (column instanceof HTMLElement) {
      column.hidden = true;
      column.replaceChildren();
    }
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts().slice(0, MAX_RECENTLY_VIEWED_PRODUCTS);
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = async () => {
    const { predictiveSearchResults, searchInput } = this.refs;
    const emptySectionId = 'predictive-search-empty';

    this.#currentIndex = -1;
    searchInput.value = '';
    this.#hideResetButton();

    if (this.#activeCountFetch) {
      this.#activeCountFetch.abort();
      this.#activeCountFetch = null;
    }

    const abortController = this.#createAbortController();
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false, url);
    const parsedEmptySectionMarkup = new DOMParser()
      .parseFromString(emptySectionMarkup, 'text/html')
      .querySelector('.predictive-search-empty-section');

    if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

    /** This needs to be awaited and not .then so the DOM is already morphed
     * when #closeResults is called and therefore the height is animated */
    const viewedProducts = RecentlyViewed.getProducts();

    if (viewedProducts.length > 0) {
      const recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup();

      if (recentlyViewedMarkup) {
        const parsedRecentlyViewedMarkup = new DOMParser().parseFromString(recentlyViewedMarkup, 'text/html');
        const recentlyViewedProductsHtml = parsedRecentlyViewedMarkup.getElementById('predictive-search-products');
        const recentlyViewedColumn = parsedEmptySectionMarkup.querySelector('#predictive-search-recently-viewed-column');

        if (recentlyViewedProductsHtml && recentlyViewedColumn instanceof HTMLElement) {
          recentlyViewedColumn.replaceChildren(...recentlyViewedProductsHtml.children);
        }
      }
    } else {
      const recentlyViewedColumn = parsedEmptySectionMarkup.querySelector('#predictive-search-recently-viewed-column');
      if (recentlyViewedColumn instanceof HTMLElement) {
        recentlyViewedColumn.hidden = true;
      }
    }

    if (abortController.signal.aborted) return;

    morph(predictiveSearchResults, parsedEmptySectionMarkup);

    if (viewedProducts.length > 0) {
      this.#showRecentlyViewedColumn();
    } else {
      this.#hideRecentlyViewedColumn();
    }

    this.#resetScrollPositions();
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
